import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ChevronDown, FileText, Zap } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import ProductPicker from "@/components/ProductPicker";
import DeleteButton from "@/components/DeleteButton";
import { useAuth } from "@/context/AuthContext";
import { prettyCat, buildProductName } from "@/lib/catalog";

const UNITS = ["pcs", "sqft", "boxes"];

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  customer_name: "", customer_phone: "",
  category: "terrazzo_tiles", variant: "", color: "", size: "", custom_size: "",
  product_name: "", unit: "pcs",
  quantity: "", price_per_unit: "", vehicle_no: "", notes: "",
};

export default function Dispatch() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/dispatch").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/dispatch/${id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sango_invoice_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice ready");
    } catch { toast.error("Invoice failed"); }
  };

  const save = async (keepOpen = false) => {
    if (!form.customer_name || !form.quantity) { toast.error("Customer & quantity required"); return; }
    try {
      await api.post("/dispatch", {
        date: form.date,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone || null,
        product_category: form.category,
        product_name: form.product_name || buildProductName(form),
        variant: form.variant || null,
        color: form.color || null,
        size: form.size || null,
        custom_size: form.custom_size || null,
        quantity: parseFloat(form.quantity),
        unit: form.unit,
        price_per_unit: form.price_per_unit ? parseFloat(form.price_per_unit) : null,
        vehicle_no: form.vehicle_no || null,
        notes: form.notes || null,
      });
      toast.success("Dispatch recorded · Stock updated");
      if (keepOpen) {
        setForm({ ...form, quantity: "", notes: "" });
      } else {
        setOpen(false);
        setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Recent unique customers for one-tap re-fill
  const recentCustomers = React.useMemo(() => {
    const seen = new Set(); const out = [];
    for (const r of rows) {
      if (!r.customer_name) continue;
      const k = r.customer_name.toLowerCase().trim();
      if (seen.has(k)) continue;
      seen.add(k); out.push(r);
      if (out.length >= 5) break;
    }
    return out;
  }, [rows]);

  const applyCustomer = (r) => setForm({ ...form, customer_name: r.customer_name, customer_phone: r.customer_phone || "" });

  const totalPreview = (parseFloat(form.price_per_unit) || 0) * (parseFloat(form.quantity) || 0);

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Sales · Outbound</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black">Dispatch to Customers</h1>
            <p className="text-[#6E6E6A] text-sm mt-2">Every entry reduces finished-product stock automatically.</p>
          </div>
          <div className="flex gap-2">
            <ExportButton module="dispatch" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-dispatch" className="bg-[#1C1C1A] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> New dispatch</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Record dispatch</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div><Label className="text-xs">Vehicle no.</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="UP-25 …" /></div>
                  <div className="col-span-2"><Label className="text-xs">Customer name *</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="disp-customer" /></div>
                  <div className="col-span-2"><Label className="text-xs">Customer phone</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
                  {recentCustomers.length > 0 && (
                    <div className="col-span-2">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A] mb-1 flex items-center gap-1"><Zap className="w-3 h-3" /> Recent customers</div>
                      <div className="flex flex-wrap gap-1.5">
                        {recentCustomers.map((c, i) => (
                          <button key={i} type="button" onClick={() => applyCustomer(c)} className="text-xs px-2.5 py-1 rounded-full border border-[#D8D8D5] hover:bg-[#1C1C1A] hover:text-white transition-colors">
                            {c.customer_name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4"><ProductPicker value={form} onChange={setForm} testidPrefix="disp" /></div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Quantity *</Label>
                    <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="disp-qty" className="h-11 text-base" />
                  </div>
                  <div>
                    <Label className="text-xs">Unit</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-2">{form.unit}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent>{UNITS.map(u => <DropdownMenuItem key={u} onClick={() => setForm({ ...form, unit: u })}>{u}</DropdownMenuItem>)}</DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div>
                    <Label className="text-xs">Rate ₹/unit</Label>
                    <Input type="number" value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })} data-testid="disp-rate" className="h-11" />
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between bg-[#F1F1EF] border border-[#D8D8D5] rounded-md px-3 py-2">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">Amount</span>
                  <span className="font-display font-black text-lg text-[#1C1C1A]">₹{totalPreview.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="mt-3"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="flex gap-2">
                  <Button onClick={() => save(false)} data-testid="disp-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white h-12 flex-1">Save dispatch</Button>
                  <Button onClick={() => save(true)} variant="outline" data-testid="disp-save-add" className="mt-4 h-12">+ Another</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className="mt-8 border border-[#D8D8D5] shadow-none rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Product</TableHead>
              <TableHead>Qty</TableHead><TableHead>Rate</TableHead><TableHead>Amount</TableHead>
              <TableHead>Vehicle</TableHead><TableHead>By</TableHead><TableHead>Invoice</TableHead>
              {user?.role === "admin" && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={user?.role === "admin" ? 10 : 9} className="text-center text-[#6E6E6A] py-8">No dispatches yet</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell className="font-medium">{r.customer_name}{r.customer_phone && <span className="block text-xs text-[#6E6E6A]">{r.customer_phone}</span>}</TableCell>
                  <TableCell>
                    <span className="uppercase text-[10px] tracking-[0.2em] text-[#6E6E6A]">{prettyCat(r.product_category)}</span>
                    {r.product_name && <span className="block text-xs">{r.product_name}</span>}
                  </TableCell>
                  <TableCell>{r.quantity} {r.unit}</TableCell>
                  <TableCell>{r.price_per_unit ? `₹${r.price_per_unit}` : "—"}</TableCell>
                  <TableCell className="font-medium">{r.total_amount ? `₹${r.total_amount}` : "—"}</TableCell>
                  <TableCell className="text-xs">{r.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-xs">{r.created_by_name}</TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => downloadInvoice(r.id)} data-testid={`invoice-${r.id}`} className="h-7 gap-1">
                      <FileText className="w-3 h-3" /> PDF
                    </Button>
                  </TableCell>
                  {user?.role === "admin" && (
                    <TableCell><DeleteButton endpoint={`/dispatch/${r.id}`} label="dispatch" onDone={load} testid={`del-disp-${r.id}`} /></TableCell>
                  )}
                  {user?.role === "supervisor" && (
                    <TableCell></TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
